// conciliatron API response shapes (camelCase, matching the backend
// dto + model in internal/reconciliations/{dto,model}.go).
//
// The reconciliation model is flat (SPEC/1573): a reconciliation carries
// its own config, status and last execution result — there is no separate
// instance/run resource. Executing dispatches the worker (async); the result
// lands back on the reconciliation's `executionResult` and `statusId` flips
// to a terminal value, which the client discovers by polling GET.

import type { DashboardConfig } from '@micronet/dashboard-kit';

import type { ReportBlock } from '../lib/reportBlocks';
import type { BatchRequest } from './compute';
import type { AlgorithmResultEnvelope } from './results';

/**
 * Status catalog ids (seeds/01_reconciliation_statuses.sql).
 *
 * EXECUTION ONLY (SPEC/2102 D4). Publishing is `publishedAt` and sending is a
 * view's `sentAt` — neither is a status. Id 4 is deprecated: migration 0004
 * lowered every row that carried it, so nothing reaches it any more. It stays
 * named because `audit_records` history points at it, and because a gap in the
 * catalog invites someone to reuse the number.
 */
export const ReconciliationStatus = {
  New: 1,
  Executed: 2,
  Error: 3,
  /** @deprecated No row carries this since migration 0004. Use isSent(view). */
  Published: 4,
  Queued: 5,
} as const;

/** A status is terminal once an execution has settled (succeeded or failed). */
export const isTerminalStatus = (statusId: number): boolean =>
  statusId === ReconciliationStatus.Executed || statusId === ReconciliationStatus.Error;

/** A reconciliation holds a plottable result once its execution succeeded. */
export const hasResultStatus = (statusId: number): boolean =>
  statusId === ReconciliationStatus.Executed;

/**
 * True while a run is in flight (enqueued/executing) — no result yet. Distinct
 * from hasBeenExecuted ("has a settled result"): a pending run has neither
 * settled nor a result, but the UI still shows a live Results tab
 * ("Ejecutando…") and resumes polling on reopen. Kept an explicit predicate so
 * the "run in flight" concept has one home.
 */
export const isPending = (statusId: number): boolean => statusId === ReconciliationStatus.Queued;

/**
 * True once an execution has been attempted: Executed or Error.
 * Drives the "land on results, not config" rule when re-opening a
 * reconciliation. Defined by inclusion (like isTerminalStatus / hasResultStatus)
 * so a future intermediate status (e.g. Queued/Running) is NOT misclassified as
 * executed — which would land the user on an empty results tab.
 *
 * Published is gone from all three predicates because it is gone from the enum's
 * live range: a published reconciliation keeps whatever execution status it had.
 */
export const hasBeenExecuted = (statusId: number): boolean =>
  statusId === ReconciliationStatus.Executed || statusId === ReconciliationStatus.Error;

const STATUS_LABEL: Record<number, string> = {
  [ReconciliationStatus.New]: 'Sin ejecutar',
  [ReconciliationStatus.Executed]: 'Ejecutado',
  [ReconciliationStatus.Error]: 'Error',
  // Unreachable since migration 0004; kept so an old audit row still renders as
  // something a person can read.
  [ReconciliationStatus.Published]: 'Publicado',
  // Queued is deliberately absent from isTerminalStatus (the poll must keep
  // going while queued) and hasResultStatus (no result yet).
  [ReconciliationStatus.Queued]: 'En cola',
};

/**
 * The one place a status id becomes human text. NOTE: its only consumers are
 * ReconciliationSidebar / ReconciliationListView, both unmounted since the
 * Historial rework — the live UI labels statuses independently in
 * HistoryPrimitives.STATUS_META, so this is not the single source it reads like.
 */
export const statusLabel = (statusId: number): string => STATUS_LABEL[statusId] ?? '—';

/** Required-name rule, shared by the editor (inline) and the store guard. */
export const REQUIRED_NAME_MESSAGE = 'El nombre de la reconciliación es obligatorio.';
export const validateName = (name: string): string | null =>
  name.trim() === '' ? REQUIRED_NAME_MESSAGE : null;

/**
 * A view's name rule, shared by the "Nueva vista" dialog and the store guard.
 *
 * Required and length-capped, mirroring validateViewCreate on the backend — the
 * limit is the same 200 the Go side uses. Separate from validateName because the
 * message names the thing: a person creating a presentation is not being told the
 * reconciliation needs a name.
 *
 * Why a person names it at all: the name used to be derived from the names already
 * stored, which is a read-then-insert on a value that has to be unique, and two
 * concurrent creates wrote the same one. See SPEC/2102 D2.
 */
export const REQUIRED_VIEW_NAME_MESSAGE = 'Ponle un nombre a la vista.';
/** One constant for one backend limit: Go caps both a reconciliation's name and
 *  a view's at the same `maxNameLen` (internal/reconciliations/validation.go).
 *
 *  The UNIT matches too, which it did not at first: Go counts characters
 *  (`tooLong` → `utf8.RuneCountInString`) because `length` here counts what a
 *  person typed. Measuring bytes there refused a Spanish name of ~180 characters
 *  while this field still said 200. */
export const NAME_MAX_LEN = 200;

/** What view #1 is called when the publish dialog's field arrives blank. Mirrors
 *  the backend's own fallback (`firstViewName`, view_service.go), which is what
 *  actually applies — this copy exists only so the dialog can prefill the field
 *  and name the fallback in its hint.
 *
 *  Two languages, one string: nothing can share it at build time, so
 *  api.test.ts reads the Go constant and fails if the two drift. It lives here,
 *  next to NAME_MAX_LEN, because this is where constants that mirror a backend
 *  value belong — not in the one component that happens to render it. */
export const FIRST_VIEW_NAME = 'Vista 1';
export const VIEW_NAME_TOO_LONG_MESSAGE = `El nombre no puede pasar de ${NAME_MAX_LEN} caracteres.`;

/** Length in Unicode code points, which is what Go's utf8.RuneCountInString
 *  counts. `String.length` counts UTF-16 code units, so it weighs an astral
 *  character (an emoji, or any script outside the BMP) as 2 — the browser used
 *  to refuse names the backend would have taken. Iterating a string yields code
 *  points, so the spread is the exact equivalent.
 *
 *  It is NOT grapheme clusters: an emoji with a skin-tone modifier still counts
 *  2, here and in Go alike. Matching the backend is the point; matching a
 *  person's intuition about "how many characters I typed" is a different
 *  problem, and one the backend would have to move first. */
const codePointLength = (s: string): number => [...s].length;

export const validateViewName = (name: string): string | null => {
  const trimmed = name.trim();
  if (trimmed === '') return REQUIRED_VIEW_NAME_MESSAGE;
  if (codePointLength(trimmed) > NAME_MAX_LEN) return VIEW_NAME_TOO_LONG_MESSAGE;
  return null;
};

/** Name for a copy — of a reconciliation (Clonar) or of a view (Nueva vista,
 *  which duplicates the one on screen). Truncated to the backend's cap so the
 *  suggestion never opens a dialog already invalid; an unnamed source copies to
 *  a bare "Copia". */
export const copyName = (name: string): string => {
  const base = name.trim() ? `Copia de ${name}` : 'Copia';
  // Sliced by code point, for the same reason validateViewName counts by code
  // point — and here it is not just a stricter cap: `slice` on UTF-16 units can
  // cut through a surrogate pair, leaving a lone surrogate that renders as a
  // replacement glyph the user has to delete by hand.
  const points = [...base];
  return points.length > NAME_MAX_LEN ? points.slice(0, NAME_MAX_LEN).join('') : base;
};

/** Failure payload the backend stores in `executionResult` when `statusId === Error`. */
export interface ExecutionError {
  error?: unknown;
}

/**
 * `execution_result` is the array of per-algorithm result envelopes on success
 * (`statusId === Executed`), or an `{ error }` object on failure (`Error`).
 * Discriminate by `statusId` before reading it as results.
 */
export type ExecutionResult = AlgorithmResultEnvelope[] | ExecutionError;

export interface Reconciliation {
  id: number;
  workspaceId: number;
  name: string;
  description: string;
  /** Free JSON on the wire; the frontend authors it as a BatchRequest. */
  config: BatchRequest;
  statusId: number;
  /**
   * THE LOCK (SPEC/2102 D1). Non-null means config and executionResult are
   * sealed: no re-execute, no parameter change. Read it through isPublished,
   * never as a status id.
   */
  publishedAt?: string | null;
  /**
   * When the FIRST of this reconciliation's views was sent — the badge the
   * list shows. Which view and to whom lives on the view itself.
   *
   * This is what `publishedAt` used to mean before migration 0004 renamed it.
   */
  sentAt?: string | null;
  /**
   * See {@link ExecutionResult}: results array on success, `{ error }` on
   * failure, absent/null on a `New` row (never executed). Read it through the
   * `statusId` guards (hasResultStatus / Array.isArray) — the shape correlates
   * with the status but is not type-level enforced.
   */
  executionResult?: ExecutionResult | null;
  /**
   * The config the worker actually ran, frozen by the backend in the same
   * transaction that dispatched the execution. Distinct from `config`, which is
   * whatever the user last saved — and saving without re-executing is normal, so
   * the two diverge routinely. Anything that pairs a result envelope with its
   * algorithm entry must read THIS one; see lib/executedConfig.
   *
   * `omitempty` on the wire, so absent on a row that never ran and on rows
   * executed before the backend started freezing it.
   */
  executionConfig?: BatchRequest;
  executedAt?: string | null;
  /**
   * The charts a partial run in flight computes (Bug 2609) — absent when none
   * is. A partial run leaves `statusId` alone (the other charts' result is still
   * the truth), so THIS is what says the run has not settled yet.
   */
  pendingInstanceIds?: string[];
  /**
   * The stored result was last written by a partial run, so it is not one run of
   * the whole config. Publishing refuses it until a full run.
   */
  executionPartial?: boolean;
  /** A failed partial run's `{ error }`, kept beside the result it left untouched. */
  lastRunError?: ExecutionError | null;
  /** The presentation while the reconciliation is open (D10) — shape without
   *  data. Publishing copies it into view #1. */
  workingDashboardConfig?: DashboardConfig;
  /**
   * This reconciliation's recorded actions, newest first (SPEC/2067).
   *
   * ONLY the listing carries them: `GET /reconciliations` merges each row's
   * history, and every single-row response omits the field. So ABSENT means
   * "this response does not talk about the history" and `[]` means "no actions"
   * — a distinction the local upsert depends on, see upsertList.
   */
  tracks?: ReconciliationTrackEntry[];
  createdBy?: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * The nine mutations the backend records (SPEC/2067). Closed catalog: the
 * column carries a CHECK, so a tenth value cannot arrive — which is what lets
 * the UI map each one to a label instead of showing the raw code.
 */
export type TrackAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'execute'
  | 'publish'
  | 'view_create'
  | 'view_update'
  | 'view_send'
  | 'view_delete';

/** One recorded action: who did what, to which object, and when. */
export interface ReconciliationTrackEntry {
  id: number;
  /** The actor's id. Absent when the actor could not be resolved — the action
   *  is still recorded, because losing the event is worse than losing the name. */
  userId?: number;
  action: TrackAction;
  /** The route TEMPLATE that was called ("POST …/views/{viewId}/send"), not the
   *  resolved path. Kept for auditing; the UI names the object instead. */
  endpoint: string;
  /** The view a `view_*` action acted on, and its name — added by migration
   *  0008. Absent on the five reconciliation-level actions, and on the `view_*`
   *  rows written BEFORE that migration, which carry no id and so cannot say
   *  which view they were about. A soft-deleted view still resolves its name,
   *  which is what lets the timeline say "eliminó la Vista D" instead of
   *  leaving a hole where its name was. */
  viewId?: number;
  viewName?: string;
  performedAt: string;
}

export interface ReconciliationListResponse {
  items: Reconciliation[];
}

export interface CreateReconciliationBody {
  name: string;
  description: string;
  config: BatchRequest;
}

/** Sparse update — only present fields are applied. */
export interface UpdateReconciliationBody {
  name?: string;
  description?: string;
  config?: BatchRequest;
  /** The presentation of an OPEN reconciliation (D10): chart shapes without
   *  their data. Publishing copies it into view #1. Omitting it leaves the
   *  stored shape alone, which is what a plain rename must do. */
  workingDashboardConfig?: DashboardConfig;
}

// ── Versions (SPEC/2102 §5) ─────────────────────────────────────────────────
//
// A published reconciliation has N views: the same sealed numbers, presented
// differently and annotated differently. Each one composes its own report and
// can be sent or not.
//
// This replaces "snapshot", which named two different things — the autosave and
// the published record — and whose ambiguity is what produced US 2100: the
// client picked the newest row and asked it for a PDF only the published one had.

/** A distribution recipient recorded when a view is sent. */
export interface Recipient {
  name?: string;
  email: string;
}

/** Notes around one figure, keyed by chart id (stable since PR 1021). */
export interface ChartNote {
  before?: string;
  after?: string;
}

export interface ReconciliationView {
  id: number;
  reconciliationId: number;
  workspaceId: number;
  /** "Vista 1" by default; renameable. This is the view's identity — the
   *  order is createdAt, and there is no numeric view column. */
  name: string;
  /** Shape without data (stripChartData). Safe only because publishing sealed
   *  the result, so the data it re-attaches to cannot change underneath it. */
  dashboardConfig: DashboardConfig;
  chartNotes: Record<string, ChartNote>;
  /** The report document. Opaque JSONB on the wire so its model can change
   *  without a migration; the frontend owns the shape (lib/reportBlocks). */
  reportBlocks: ReportBlock[];
  /** The report's title, and the PDF's download filename. */
  title: string;
  /** storage-hub file id of the sent PDF. Absent while the view is a draft. */
  documentKey?: string;
  distributionList: Recipient[];
  /** Non-null once sent, which FREEZES the view (D5): its PDF, its link and
   *  what it shows on screen agree from then on. */
  sentAt?: string | null;
  sentBy?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ViewListResponse {
  items: ReconciliationView[];
}

/** Body of POST .../publish. Naming view #1 is the only choice it offers.
 *  The field was `versionName` until the rename to views, which the backend
 *  reads as `viewName` (view_dto.go) — so a name sent from here was silently
 *  dropped. Nothing sent one yet; the publish dialog does now. */
export interface PublishBody {
  viewName?: string;
}

/** Body of POST .../views. The name is REQUIRED: a view is named by whoever
 *  creates it, and the backend refuses a blank one with 400 rather than
 *  inventing one (validateViewCreate). `copyFrom` duplicates an existing
 *  view's shape, notes and report. */
export interface CreateViewBody {
  name: string;
  copyFrom?: number;
}

/** Sparse body of PUT .../views/{vid} — only present fields are applied. */
export interface UpdateViewBody {
  name?: string;
  title?: string;
  dashboardConfig?: DashboardConfig;
  chartNotes?: Record<string, ChartNote>;
  reportBlocks?: ReportBlock[];
}

/** Body of POST .../views/{vid}/send. The PDF is rendered and uploaded by
 *  the browser, so what travels is its storage-hub file id. */
export interface SendViewBody {
  documentKey: string;
  distributionList?: Recipient[];
}

// ── The two lifecycle predicates, consumed by everyone (SPEC/2102 §6) ────────

/** Sealed: no re-execute, no parameter change. Cloning is the way to change the
 *  substance. */
export const isPublished = (rec: Reconciliation | null | undefined): boolean =>
  rec?.publishedAt != null;

/**
 * Why an editing control is dead on a published reconciliation, and what to do
 * instead. Lives beside the predicate rather than in a component so the store's
 * refusal and the band that explains it are the same sentence — a user who reads
 * "no se pudo guardar: X" and then a band saying Y has been told two things.
 *
 * It names the escape hatch on purpose: the seal is irreversible (D1), so
 * reporting the block without saying that cloning is the way forward leaves the
 * user stuck. And it names what is NOT sealed, because the label is editable
 * (D7) and a band that says "sellada" full stop sends the user to clone for a
 * typo — which is the expensive answer to the cheap problem.
 */
export const SEALED_ANALYSIS_MESSAGE =
  'Esta reconciliación está publicada: su configuración y su resultado quedaron sellados. ' +
  'El nombre y la descripción sí se pueden cambiar. Para cambiar el análisis, clónala.';

/** Frozen: a sent view accepts no edits and cannot be deleted. */
export const isSent = (v: ReconciliationView | null | undefined): boolean =>
  v?.sentAt != null;
